// Standalone PDF export for the 4-week schedule grid. Browser
// print is fine for casual paper output, but a real PDF gives
// supervisors:
//   * a downloadable artifact they can attach to email / message
//   * deterministic layout independent of the local browser's
//     print preferences (margins, headers/footers, "fit to page")
//   * Type-set tables with consistent column widths instead of the
//     browser's print-mode `zoom` scaling.
//
// Each PDF page covers two weeks (14 days) — matches the print
// layout in Schedule.tsx and lets every chip read at near-screen
// size on a landscape letter page. For each page we render one
// jspdf-autotable per shift section so the section's color bar +
// officer rows + coverage rows + OIC row stay grouped together
// (autotable will page-break a section across pages if it doesn't
// fit, but for typical agency sizes it doesn't).

import { jsPDF } from "jspdf";
import autoTable, { type RowInput } from "jspdf-autotable";
import { format } from "date-fns";
import type { Assignment, Employee, Shift, ShiftCode } from "./types";
import { readableTextOn } from "./colors";

export interface ScheduleCoverageRow {
  assigned: number;
  minStaffing: number;
  oicAssigned: number;
  oicRequired: number;
  overridden: boolean;
}

export interface ScheduleGroup {
  shift: Shift | null;
  label: string;
  employees: Employee[];
}

export interface SchedulePdfInput {
  agencyName: string;
  departmentName: string | null;
  days: Date[];
  groups: ScheduleGroup[];
  // Per-(empId|date) display text the grid would show in the cell.
  // Caller resolves the priority (XXX / V / OT / IOD / 9-21 / …)
  // and hands us the final string — keeps schedule-side render
  // logic in one place.
  cellText: (emp: Employee, date: Date, group: ScheduleGroup) => string;
  cellBg: (emp: Employee, date: Date, group: ScheduleGroup) => string | null;
  // (shiftId|date) → coverage row data, or null when N/A.
  coverageAt: (shift: Shift, date: Date) => ScheduleCoverageRow | null;
  // Whether the shift is in rotation on this date (tac transition
  // + per-weekday opt-out). Inactive dates render an em-dash in
  // the coverage / OIC cells.
  isShiftActiveOn: (shift: Shift, date: Date) => boolean;
  // Pre-resolved set of shift codes — used to colour the cells
  // (e.g. OT chips stay amber on paper). Optional; absent means
  // every cell renders on white.
  codes?: ShiftCode[];
  // Tac-mode start date for the banner copy. Null if not scheduled.
  tacStart?: string | null;
}

const PAGE_MARGIN_PT = 18;        // ~0.25 in
const HEADER_PT = 9;
const CELL_PT = 7.5;
const HALF_DAYS = 14;

// Hex (#rrggbb) → [r, g, b] integers for jspdf's color API.
function hexToRgb(hex: string): [number, number, number] {
  const m = hex.replace("#", "");
  if (m.length !== 6) return [255, 255, 255];
  return [
    parseInt(m.slice(0, 2), 16),
    parseInt(m.slice(2, 4), 16),
    parseInt(m.slice(4, 6), 16),
  ];
}

export function generateSchedulePdf(input: SchedulePdfInput): jsPDF {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "pt",
    format: "letter",
    compress: true,
  });

  const pageW = doc.internal.pageSize.getWidth();
  const datesAll = input.days;
  const dateRangeLabel =
    `${format(datesAll[0], "MMM d, yyyy")} – ${format(datesAll[datesAll.length - 1], "MMM d, yyyy")}`;

  // Two halves: days 0-13, then 14-27.
  for (let half = 0; half < 2; half++) {
    if (half > 0) doc.addPage();
    const slice = datesAll.slice(half * HALF_DAYS, (half + 1) * HALF_DAYS);
    let cursorY = PAGE_MARGIN_PT;

    // Page header — agency name, department, date range, page label.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(15, 23, 42);
    doc.text(input.agencyName, PAGE_MARGIN_PT, cursorY + 4);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(71, 85, 105);
    const subParts = [
      input.departmentName,
      dateRangeLabel,
      `Page ${half + 1} of 2 (${half === 0 ? "weeks 1-2" : "weeks 3-4"})`,
    ].filter(Boolean);
    doc.text(subParts.join("   ·   "), PAGE_MARGIN_PT, cursorY + 18);
    if (input.tacStart) {
      doc.setTextColor(180, 83, 9);
      doc.text(
        `12-hour Shifts scheduled — regular before ${input.tacStart}, 12-hour on/after.`,
        PAGE_MARGIN_PT, cursorY + 30,
      );
      cursorY += 36;
    } else {
      cursorY += 24;
    }

    // Build the header row used by every per-section table on this
    // page so the day columns line up.
    const headRow: RowInput = [
      { content: "Employee", styles: { halign: "left", fontStyle: "bold" } },
      ...slice.map((d) => ({
        content: `${format(d, "EEE").toUpperCase()}\n${format(d, "M/d")}`,
        styles: { halign: "center" as const, fontStyle: "bold" as const },
      })),
    ];

    for (const group of input.groups) {
      // Shift colour bar above the section's officer rows. autotable
      // doesn't expose a row-merge-and-fill helper, so we draw the
      // band ourselves before the table.
      if (group.shift) {
        const bg = hexToRgb(group.shift.color ?? "#64748b");
        const fg = hexToRgb(readableTextOn(group.shift.color ?? "#64748b"));
        const bandH = 14;
        doc.setFillColor(bg[0], bg[1], bg[2]);
        doc.rect(PAGE_MARGIN_PT, cursorY, pageW - 2 * PAGE_MARGIN_PT, bandH, "F");
        doc.setTextColor(fg[0], fg[1], fg[2]);
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        const meta = group.shift
          ? ` · ${group.shift.startTime}–${shiftEndTimeHHMM(group.shift)} · ${group.employees.length} officer${group.employees.length === 1 ? "" : "s"}`
          : "";
        doc.text(group.label + meta, PAGE_MARGIN_PT + 4, cursorY + 10);
        cursorY += bandH;
      } else {
        // Unassigned header
        doc.setFillColor(241, 245, 249);
        doc.rect(PAGE_MARGIN_PT, cursorY, pageW - 2 * PAGE_MARGIN_PT, 14, "F");
        doc.setTextColor(51, 65, 85);
        doc.setFontSize(9);
        doc.setFont("helvetica", "bold");
        doc.text(group.label, PAGE_MARGIN_PT + 4, cursorY + 10);
        cursorY += 14;
      }

      const officerRows: RowInput[] = group.employees.map((emp) => {
        const radioOrBadge = emp.radioNumber ?? emp.badgeNumber;
        const name = `${emp.rank ? emp.rank + ". " : ""}${emp.lastName}, ${emp.firstName}${radioOrBadge ? "  #" + radioOrBadge : ""}`;
        return [
          { content: name, styles: { halign: "left", cellPadding: 2 } },
          ...slice.map((d) => {
            const text = input.cellText(emp, d, group);
            const bg = input.cellBg(emp, d, group);
            const bgRgb = bg ? hexToRgb(bg) : null;
            return {
              content: text || "",
              styles: {
                halign: "center" as const,
                cellPadding: 1.5,
                ...(bgRgb && { fillColor: bgRgb }),
              },
            };
          }),
        ];
      });

      // Coverage / OIC rows for shift sections only — unassigned
      // groups don't carry a roster expectation.
      const summaryRows: RowInput[] = [];
      if (group.shift) {
        const sh = group.shift;
        const covRow: RowInput = [
          { content: "Coverage", styles: { halign: "left", fontStyle: "bold", textColor: [71, 85, 105] } },
          ...slice.map((d) => {
            const active = input.isShiftActiveOn(sh, d);
            if (!active) {
              return {
                content: "—",
                styles: { halign: "center" as const, textColor: [148, 163, 184] as [number, number, number] },
              };
            }
            const cov = input.coverageAt(sh, d);
            const a = cov?.assigned ?? 0;
            const r = cov?.minStaffing ?? sh.minStaffing;
            const overridden = cov?.overridden ?? false;
            const short = !overridden && a < r;
            return {
              content: `${fmtNum(a)}/${r}${overridden ? " ✓" : ""}`,
              styles: {
                halign: "center" as const,
                ...(short && {
                  fillColor: [254, 226, 226] as [number, number, number],
                  textColor: [153, 27, 27] as [number, number, number],
                  fontStyle: "bold" as const,
                }),
              },
            };
          }),
        ];
        summaryRows.push(covRow);

        if (sh.requiresOic !== false) {
          const oicRow: RowInput = [
            { content: "OIC", styles: { halign: "left", fontStyle: "bold", textColor: [71, 85, 105] } },
            ...slice.map((d) => {
              const active = input.isShiftActiveOn(sh, d);
              if (!active) {
                return {
                  content: "—",
                  styles: { halign: "center" as const, textColor: [148, 163, 184] as [number, number, number] },
                };
              }
              const cov = input.coverageAt(sh, d);
              const a = cov?.oicAssigned ?? 0;
              const r = cov?.oicRequired ?? 1;
              const overridden = cov?.overridden ?? false;
              const short = !overridden && a < r;
              return {
                content: `${fmtNum(a)}/${r}`,
                styles: {
                  halign: "center" as const,
                  ...(short && {
                    fillColor: [254, 226, 226] as [number, number, number],
                    textColor: [153, 27, 27] as [number, number, number],
                    fontStyle: "bold" as const,
                  }),
                },
              };
            }),
          ];
          summaryRows.push(oicRow);
        }
      }

      const allBody: RowInput[] = [...officerRows, ...summaryRows];
      if (allBody.length === 0) continue;

      autoTable(doc, {
        startY: cursorY,
        margin: { left: PAGE_MARGIN_PT, right: PAGE_MARGIN_PT },
        head: [headRow],
        body: allBody,
        styles: {
          fontSize: CELL_PT,
          cellPadding: 1.5,
          lineColor: [203, 213, 225],
          lineWidth: 0.25,
        },
        headStyles: {
          fontSize: HEADER_PT,
          fillColor: [248, 250, 252],
          textColor: [71, 85, 105],
          lineColor: [148, 163, 184],
          lineWidth: 0.5,
        },
        columnStyles: {
          0: { cellWidth: 110, halign: "left" },
        },
        theme: "grid",
      });
      // autotable updates lastAutoTable.finalY; advance cursorY.
      const finalY = (doc as unknown as {
        lastAutoTable?: { finalY: number };
      }).lastAutoTable?.finalY ?? cursorY;
      cursorY = finalY + 4;
    }
  }

  return doc;
}

function fmtNum(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

// "HH:MM" + duration → end time "HH:MM" (wrap-midnight aware).
function shiftEndTimeHHMM(s: Shift): string {
  const [h, m] = s.startTime.split(":").map(Number);
  const total = (h * 60 + m + s.durationMin) % (24 * 60);
  return `${String(Math.floor(total / 60)).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
}

// Re-export for the caller's convenience (avoids needing to also
// import jspdf upstream to type the return value).
export type ScheduleAssignment = Assignment;
